/**
 * Page View Tracker
 * Tracks page views on route changes
 */

import { analyticsApiService } from '@/shared/services/analyticsApiService';
import { CookieManager } from './cookies';
import { logger } from './index';

// Session ID storage key
const SESSION_ID_KEY = 'aditas_session_id';

/**
 * Generate or retrieve session ID
 */
function getOrCreateSessionId(): string {
  let sessionId = CookieManager.get(SESSION_ID_KEY);
  if (!sessionId) {
    // Generate new session ID
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    // Store in cookie (expires in 30 minutes of inactivity)
    CookieManager.set(SESSION_ID_KEY, sessionId, {
      expires: 0.5, // 30 minutes
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'lax'
    });
  }
  return sessionId;
}

/**
 * Get device type from user agent
 */
function getDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Get browser name from user agent
 */
function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Unknown';
}

/**
 * Get OS from user agent
 */
function getOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

/**
 * Track a page view
 */
export async function trackPageView(
  pagePath: string,
  pageTitle?: string,
  additionalData?: {
    time_on_page?: number;
    scroll_depth?: number;
    tracking_metadata?: Record<string, unknown>;
  }
): Promise<void> {
  try {
    // Get referrer (previous page)
    const referrer = document.referrer || undefined;
    
    // Get session ID
    const sessionId = getOrCreateSessionId();
    
    // Track page view
    await analyticsApiService.trackPageView({
      page_path: pagePath,
      page_title: pageTitle || document.title,
      referrer: referrer,
      user_agent: navigator.userAgent,
      session_id: sessionId,
      device_type: getDeviceType(),
      browser: getBrowser(),
      os: getOS(),
      time_on_page: additionalData?.time_on_page,
      scroll_depth: additionalData?.scroll_depth,
      tracking_metadata: additionalData?.tracking_metadata,
    });
    
    logger.debug('Page view tracked', 'PageViewTracker', { pagePath, pageTitle });
  } catch (error) {
    // Silently fail - don't interrupt user experience
    logger.warn('Failed to track page view', 'PageViewTracker', { error, pagePath });
  }
}

/**
 * Track time on page when user leaves
 */
let pageViewStartTime: number | null = null;
let maxScrollDepth = 0;

export function startPageViewTracking(pagePath: string, pageTitle?: string): () => void {
  pageViewStartTime = Date.now();
  maxScrollDepth = 0;
  
  // Track scroll depth
  const trackScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollPercentage = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
    maxScrollDepth = Math.max(maxScrollDepth, scrollPercentage);
  };
  
  window.addEventListener('scroll', trackScroll, { passive: true });
  
  // Track when user leaves page
  const handleBeforeUnload = () => {
    if (pageViewStartTime) {
      const timeOnPage = Math.round((Date.now() - pageViewStartTime) / 1000); // seconds
      trackPageView(pagePath, pageTitle, {
        time_on_page: timeOnPage,
        scroll_depth: maxScrollDepth,
      });
    }
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Clean up on navigation (for SPA)
  return () => {
    window.removeEventListener('scroll', trackScroll);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    
    if (pageViewStartTime) {
      const timeOnPage = Math.round((Date.now() - pageViewStartTime) / 1000);
      trackPageView(pagePath, pageTitle, {
        time_on_page: timeOnPage,
        scroll_depth: maxScrollDepth,
      });
    }
  };
}

